import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';

import { GetCompanyQuery } from '@neo.tax/shared/graphql';

/**
 * This hook identifies a company to launch darkly.  It is a blocking step when loading a company because
 * it will result in flags being updated if any flags are targeting the company.
 */
export const useLaunchDarklyIdentifyCompany = (company: GetCompanyQuery['getCompany'] | null): boolean => {
  const ldClient = useLDClient();
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState(company?.id);

  useEffect(() => {
    let isMounted = true;

    const targetCompany = async (): Promise<void> => {
      // reset the loading status so on subsequent re-renders
      // after the company-id changes, we still report true
      setLoading(true);
      setCompanyId(company?.id);

      if (!ldClient || !company) {
        return;
      }

      await ldClient.identify({
        kind: 'company',
        key: company.id,
        name: company.shortName,
      });

      // if someone navigates away, don't reset the loading status
      if (isMounted) {
        setLoading(false);
      }
    };

    targetCompany();

    return (): void => {
      // reset on dismount so we don't try changing state if an
      // orphaned call to ldClient.identify completes
      isMounted = false;
    };
  }, [company, ldClient]);

  // since the use-effect is async, we need to force communicating
  // the loading status change synchronously on company-id change
  return loading || companyId !== company?.id;
};
